import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Activity, Clock3, MapPin, Phone, Smartphone, User2, X, CreditCard, AlertTriangle, BarChart3, ArrowLeft, Calendar, ChevronRight, Info } from 'lucide-react';
import type { UserAnalytics, UserAnalyticsTimelineEvent, UserAnalyticsSession, SessionRangeFilter, UserAuthActivitySession } from '../../services/MasterAdmin/userService';
import { fetchUserAnalytics, type FetchUserAnalyticsParams } from '../../services/MasterAdmin/userService';
import { fetchUserHealthRecordUsage, type HealthRecordUsageByType } from '../../services/MasterAdmin/healthRecordUsageService';
import { fetchActorObservabilityDashboard, type ActorDashboardResponse } from '../../services/MasterAdmin/observabilityService';
import { RecipientNotificationsList } from './RecipientNotificationsList';
import PermissionTable from './PermissionTable';

interface UserDetailsModalProps {
  user: any;
  isOpen: boolean;
  onClose: () => void;
  /** When true, show as full page: back icon instead of close, wider layout */
  standalone?: boolean;
  headerActions?: React.ReactNode;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ user, isOpen, onClose, standalone = false, headerActions }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { defaultTab?: string } | null;
  const [analytics, setAnalytics] = useState<UserAnalytics | null>(null);
  const [storageUsage, setStorageUsage] = useState<{
    storageLimitBytes: number;
    totalRecords: number;
    totalBytes: number;
    remainingBytes: number;
    usedPercent: number;
    byType: HealthRecordUsageByType[];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingStorageUsage, setLoadingStorageUsage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'orders-payments' | 'behavior-risk' | 'timeline' | 'auth-activity' | 'storage-usage' | 'error-log' | 'api-metrics' | 'notifications' | 'permissions'>((locationState?.defaultTab as any) || 'overview');
  const [timelinePage, setTimelinePage] = useState(1);
  const [sessionRange, setSessionRange] = useState<SessionRangeFilter>('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [actorDashboard, setActorDashboard] = useState<ActorDashboardResponse['data'] | null>(null);
  const [loadingActorDashboard, setLoadingActorDashboard] = useState(false);
  const TIMELINE_PAGE_SIZE = 50;

  const safe = (value: any): string => {
    if (value === null || value === undefined) return 'NA';
    const str = String(value).trim();
    return str.length === 0 ? 'NA' : str;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'NA';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string | null | undefined) => {
    if (dateString == null || dateString === '') return '—';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatBytes = (bytes: number) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const idx = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / 1024 ** idx;
    return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${units[idx]}`;
  };

  const formatSessionDate = (dateString: string | null | undefined) => {
    if (dateString == null || dateString === '') return '—';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-IN', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const loadAnalytics = async (params?: FetchUserAnalyticsParams) => {
    if (!user?.userId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await fetchUserAnalytics(user.userId, params);
      if (res.success) {
        setAnalytics(res.data);
      } else {
        setError('Failed to load user analytics');
      }
    } catch (e) {
      console.error('Error loading user analytics', e);
      setError('Failed to load user analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !user?.userId) return;
    const params: FetchUserAnalyticsParams = { range: sessionRange };
    if (sessionRange === 'custom' && customFrom) params.from = customFrom;
    if (sessionRange === 'custom' && customTo) params.to = customTo;
    loadAnalytics(params);
    setTimelinePage(1);
  }, [isOpen, user?.userId, sessionRange, customFrom, customTo]);

  useEffect(() => {
    if (!isOpen || !user?.userId) return;
    if (activeTab !== 'error-log' && activeTab !== 'api-metrics') return;
    let mounted = true;
    (async () => {
      try {
        setLoadingActorDashboard(true);
        const params: { from?: string; to?: string } = {};
        if (sessionRange === 'custom' && customFrom) params.from = customFrom;
        if (sessionRange === 'custom' && customTo) params.to = customTo;
        const res = await fetchActorObservabilityDashboard(user.userId, params);
        if (!mounted) return;
        setActorDashboard(res.data);
      } catch (e) {
        console.error('Error loading actor observability dashboard', e);
      } finally {
        if (mounted) setLoadingActorDashboard(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isOpen, user?.userId, activeTab, sessionRange, customFrom, customTo]);

  useEffect(() => {
    if (!isOpen || !user?.userId) return;
    let mounted = true;
    (async () => {
      try {
        setLoadingStorageUsage(true);
        const res = await fetchUserHealthRecordUsage(user.userId);
        if (!mounted) return;
        setStorageUsage(res.data);
      } catch (e) {
        console.error('Error loading user storage usage', e);
      } finally {
        if (mounted) setLoadingStorageUsage(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isOpen, user?.userId]);

  const storageTypeTotalBytes = (storageUsage?.byType || []).reduce((sum, row) => sum + Number(row.bytes || 0), 0);
  const storagePie = (() => {
    if (!storageUsage) return 'conic-gradient(#e5e7eb 0 100%)';
    const used = Math.max(0, Math.min(100, Number(storageUsage.usedPercent || 0)));
    const remaining = 100 - used;
    return `conic-gradient(#3b82f6 0 ${used}%, #e5e7eb ${used}% ${used + remaining}%)`;
  })();

  const loggedInPlatforms = Array.from(
    new Set(
      (analytics?.authActivity?.recentSessions || [])
        .filter((s) => s.status === 'ACTIVE' && !!s.platform)
        .map((s) => String(s.platform).toLowerCase())
    )
  );

  const allSessionPlatforms = Array.from(
    new Set(
      (analytics?.authActivity?.recentSessions || [])
        .filter((s) => !!s.platform)
        .map((s) => String(s.platform).toLowerCase())
    )
  );

  return (
    <div className="w-full min-w-0">
      <div className={`w-full min-w-0 mx-auto ${standalone ? 'max-w-7xl' : 'max-w-5xl'}`}>
        <div className="pb-4 border-b border-gray-100 dark:border-gray-700">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            {standalone ? (
              <div className="flex min-w-0 items-center gap-3">
                <button
                  onClick={onClose}
                  className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span>Back</span>
                </button>
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">User Analytics</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Master Admin &bull; Deep profile, engagement, and risk insights
                  </p>
                </div>
              </div>
            ) : (
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">User Analytics</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Master Admin &bull; Deep profile, engagement, and risk insights
                </p>
              </div>
            )}
            {!standalone ? (
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            ) : null}
          </div>
        </div>
        <div className="space-y-6 pt-4 sm:pt-6">
          <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-start sm:space-x-4 sm:space-y-0">
            <div className="h-20 w-20 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center shadow-inner">
              {user.photo ? (
                <img
                  src={user.photo}
                  alt={user.name}
                  className="h-20 w-20 rounded-full object-cover"
                />
              ) : (
                <span className="text-2xl text-primary-800 dark:text-primary-300 font-semibold">
                  {user.name && user.name.trim().length > 0
                    ? user.name.charAt(0).toUpperCase()
                    : (user.phone_number ?? 'NA').slice(-4)}
                </span>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <User2 className="w-4 h-4 text-primary-500" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {safe(user.name) !== 'NA' ? safe(user.name) : safe(user.phone_number)}
                </h3>
              </div>
              <div className="mt-1 space-y-1 text-sm">
                <p className="text-gray-600 dark:text-gray-300 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span>{safe(user.phone_number)}</span>
                </p>
                <p className="text-gray-600 dark:text-gray-300">
                  {safe(user.emailId)}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              {analytics && (
                <div className="flex flex-col items-start gap-2 text-left sm:items-end sm:text-right">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    analytics.basicProfile.accountStatus === 'ACTIVE'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                  }`}>
                    {analytics.basicProfile.accountStatus === 'ACTIVE' ? 'Active' : 'Inactive'}
                  </span>
                  <div className="flex flex-wrap justify-end gap-2 text-xs text-gray-500 dark:text-gray-400">
                    {(loggedInPlatforms.length > 0 ? loggedInPlatforms : (allSessionPlatforms.length > 0 ? allSessionPlatforms : [analytics.basicProfile.device || 'na']))
                      .map((platform) => (
                        <span key={platform} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 capitalize">
                          <Smartphone className="w-3 h-3" />
                          {platform}
                        </span>
                      ))}
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700">
                      <Clock3 className="w-3 h-3" />
                      Joined {analytics.basicProfile.registrationDate ? formatDate(analytics.basicProfile.registrationDate) : 'NA'}
                    </span>
                  </div>
                </div>
              )}
              {headerActions ? (
                <div className="shrink-0">
                  {headerActions}
                </div>
              ) : null}
            </div>
          </div>

          {/* Tabs */}
          <div className="-mx-1 mb-4 flex gap-1 overflow-x-auto border-b border-gray-100 pb-px text-sm scrollbar-light dark:border-gray-700 sm:mx-0">
            <button
              className={`shrink-0 px-3 py-2 sm:px-4 -mb-px border-b-2 transition-colors ${
                activeTab === 'overview'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={`shrink-0 px-3 py-2 sm:px-4 -mb-px border-b-2 transition-colors ${
                activeTab === 'orders-payments'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
              onClick={() => setActiveTab('orders-payments')}
            >
              Orders & Payments
            </button>
            <button
              className={`shrink-0 px-3 py-2 sm:px-4 -mb-px border-b-2 transition-colors ${
                activeTab === 'behavior-risk'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
              onClick={() => setActiveTab('behavior-risk')}
            >
              Behavior & Risk
            </button>
            <button
              className={`shrink-0 px-3 py-2 sm:px-4 -mb-px border-b-2 transition-colors ${
                activeTab === 'timeline'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
              onClick={() => setActiveTab('timeline')}
            >
              Event Timeline
            </button>
            <button
              className={`shrink-0 px-3 py-2 sm:px-4 -mb-px border-b-2 transition-colors ${
                activeTab === 'api-metrics'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
              onClick={() => setActiveTab('api-metrics')}
            >
              API Metrics
            </button>
            <button
              className={`shrink-0 px-3 py-2 sm:px-4 -mb-px border-b-2 transition-colors ${
                activeTab === 'error-log'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
              onClick={() => setActiveTab('error-log')}
            >
              Error Log
            </button>
            <button
              className={`shrink-0 px-3 py-2 sm:px-4 -mb-px border-b-2 transition-colors ${
                activeTab === 'storage-usage'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
              onClick={() => setActiveTab('storage-usage')}
            >
              Storage usages
            </button>
            <button
              className={`shrink-0 px-3 py-2 sm:px-4 -mb-px border-b-2 transition-colors ${
                activeTab === 'auth-activity'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
              onClick={() => setActiveTab('auth-activity')}
            >
              Login Activity
            </button>
            <button
              className={`shrink-0 px-3 py-2 sm:px-4 -mb-px border-b-2 transition-colors ${
                activeTab === 'notifications'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
              onClick={() => setActiveTab('notifications')}
            >
              Notifications
            </button>
            <button
              className={`shrink-0 px-3 py-2 sm:px-4 -mb-px border-b-2 transition-colors ${
                activeTab === 'permissions'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
              onClick={() => setActiveTab('permissions')}
            >
              Permissions
            </button>
          </div>

          {/* Decide what to render below the tabs */}
          {loading && (
            <div className="py-10 flex flex-col items-center justify-center text-sm text-gray-500 dark:text-gray-400">
              <Clock3 className="w-5 h-5 mr-2 animate-spin" />
              <span>Loading user analytics...</span>
            </div>
          )}

          {!loading && error && (
            <div className="py-6 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 text-sm text-red-600 dark:text-red-300 flex items-center justify-between">
              <span>{error}</span>
              <button
                onClick={() => {
                  const params: FetchUserAnalyticsParams = { range: sessionRange };
                  if (sessionRange === 'custom' && customFrom) params.from = customFrom;
                  if (sessionRange === 'custom' && customTo) params.to = customTo;
                  loadAnalytics(params);
                }}
                className="text-xs font-semibold underline"
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && analytics && activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Top metric strip */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Total Sessions
                  </p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {analytics.basicProfile.totalSessions}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Avg session {Math.round(analytics.activitySummary.averageSessionDurationSec / 60)} min
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    App Opens
                  </p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {analytics.activitySummary.totalAppOpens}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Screens Viewed
                  </p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {analytics.activitySummary.totalScreensViewed}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Last Login
                  </p>
                  <p className="mt-2 text-xs font-semibold text-gray-900 dark:text-white">
                    {formatDateTime(analytics.authActivity?.lastLogin ?? analytics.activitySummary.lastLogin)}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Total Orders
                  </p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {analytics.basicProfile.totalOrders}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Completed {analytics.bookingHistory.completedOrders} • Cancelled {analytics.bookingHistory.cancelledOrders}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Total Payments
                  </p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {analytics.basicProfile.totalPayments}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <CreditCard className="w-3 h-3" />
                    Preferred {analytics.payments.preferredPaymentMethod || 'NA'}
                  </p>
                </div>
              </div>

              <div className="space-y-5">
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-5">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Personal Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Phone Number</label>
                      <p className="text-gray-900 dark:text-white mt-1">{safe(user.phone_number)}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">ABHA ID</label>
                      <p className="text-gray-900 dark:text-white mt-1">{safe(user.ABHA_ID)}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Date of Birth</label>
                      <p className="text-gray-900 dark:text-white mt-1">{user.dateOfBirth ? formatDate(user.dateOfBirth) : 'NA'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Gender</label>
                      <p className="text-gray-900 dark:text-white mt-1">{safe(user.gender)}</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-5">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Location & Platform</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Location</label>
                      <p className="text-gray-900 dark:text-white mt-1 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-primary-500" />
                        <span>{safe(user.location) !== 'NA' ? safe(user.location) : safe(user.city)}</span>
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">City</label>
                      <p className="text-gray-900 dark:text-white mt-1">{safe(user.city)}</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Platforms</label>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {(loggedInPlatforms.length > 0 ? loggedInPlatforms : (allSessionPlatforms.length > 0 ? allSessionPlatforms : [safe(user.platforms)]))
                          .map((platform) => (
                            <span key={platform} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-xs capitalize text-gray-700 dark:text-gray-200">
                              <Smartphone className="w-3 h-3" />
                              {platform}
                            </span>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-5">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Emergency & Account</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Emergency Contact Number</label>
                      <p className="text-gray-900 dark:text-white mt-1">{safe(user.emergencyContactNumber)}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Status</label>
                      <p className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        analytics.basicProfile.accountStatus === 'ACTIVE'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                      }`}>
                        {analytics.basicProfile.accountStatus === 'ACTIVE' ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Registered On</label>
                      <p className="text-gray-900 dark:text-white mt-1">{formatDate(user.createdAt)}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Last App Open</label>
                      <p className="text-gray-900 dark:text-white mt-1">
                        {analytics.activitySummary.lastAppOpen ? formatDateTime(analytics.activitySummary.lastAppOpen) : 'NA'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-5">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-primary-500" />
                    Services Interaction
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-3">
                      <p className="text-gray-500 dark:text-gray-400">Medicine</p>
                      <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                        {analytics.servicesInteraction.medicineFeatureViews}
                      </p>
                    </div>
                    <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-3">
                      <p className="text-gray-500 dark:text-gray-400">Blood Bank</p>
                      <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                        {analytics.servicesInteraction.bloodBankViews}
                      </p>
                    </div>
                    <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-3">
                      <p className="text-gray-500 dark:text-gray-400">Ambulance</p>
                      <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                        {analytics.servicesInteraction.ambulanceViews}
                      </p>
                    </div>
                    <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-3">
                      <p className="text-gray-500 dark:text-gray-400">Doctor Consult</p>
                      <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                        {analytics.servicesInteraction.doctorConsultationViews}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && analytics && activeTab === 'orders-payments' && (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Booking / Order History</h4>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Filter by:
                  </span>
                  <select
                    value={sessionRange}
                    onChange={(e) => setSessionRange(e.target.value as SessionRangeFilter)}
                    className="text-sm rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5"
                  >
                    <option value="day">Today</option>
                    <option value="week">Last 7 days</option>
                    <option value="month">Last 30 days</option>
                    <option value="custom">Custom range</option>
                  </select>
                  {sessionRange === 'custom' && (
                    <>
                      <input
                        type="date"
                        value={customFrom}
                        onChange={(e) => setCustomFrom(e.target.value)}
                        className="text-sm rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5"
                      />
                      <span className="text-gray-400">to</span>
                      <input
                        type="date"
                        value={customTo}
                        onChange={(e) => setCustomTo(e.target.value)}
                        className="text-sm rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5"
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Summary – all services */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Summary</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 text-sm">
                  <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Medicine</p>
                    <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                      {analytics.bookingHistory.ordersByService?.medicine ?? analytics.bookingHistory.medicineOrders}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Completed {analytics.bookingHistory.ordersByService?.medicineCompleted ?? 0} / Cancelled {analytics.bookingHistory.ordersByService?.medicineCancelled ?? 0} / Pending {analytics.bookingHistory.ordersByService?.medicinePending ?? 0}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Ambulance</p>
                    <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                      {analytics.bookingHistory.ordersByService?.ambulance ?? analytics.bookingHistory.ambulanceBookings}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Completed {analytics.bookingHistory.ordersByService?.ambulanceCompleted ?? 0} / Cancelled {analytics.bookingHistory.ordersByService?.ambulanceCancelled ?? 0} / Pending {analytics.bookingHistory.ordersByService?.ambulancePending ?? 0}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Blood Bank</p>
                    <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                      {analytics.bookingHistory.ordersByService?.bloodBank ?? analytics.bookingHistory.bloodRequests}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Completed {analytics.bookingHistory.ordersByService?.bloodBankCompleted ?? 0} / Cancelled {analytics.bookingHistory.ordersByService?.bloodBankCancelled ?? 0} / Pending {analytics.bookingHistory.ordersByService?.bloodBankPending ?? 0}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Doctor Consultation</p>
                    <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                      {analytics.bookingHistory.ordersByService?.doctorConsultation ?? 0}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Completed {analytics.bookingHistory.ordersByService?.doctorConsultationCompleted ?? 0} / Cancelled {analytics.bookingHistory.ordersByService?.doctorConsultationCancelled ?? 0} / Pending {analytics.bookingHistory.ordersByService?.doctorConsultationPending ?? 0}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Hospital</p>
                    <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                      {analytics.bookingHistory.ordersByService?.hospital ?? 0}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Completed {analytics.bookingHistory.ordersByService?.hospitalCompleted ?? 0} / Cancelled {analytics.bookingHistory.ordersByService?.hospitalCancelled ?? 0} / Pending {analytics.bookingHistory.ordersByService?.hospitalPending ?? 0}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Lab Test</p>
                    <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                      {analytics.bookingHistory.ordersByService?.labTest ?? 0}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Completed {analytics.bookingHistory.ordersByService?.labTestCompleted ?? 0} / Cancelled {analytics.bookingHistory.ordersByService?.labTestCancelled ?? 0} / Pending {analytics.bookingHistory.ordersByService?.labTestPending ?? 0}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Products</p>
                    <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                      {analytics.bookingHistory.ordersByService?.products ?? 0}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Completed {analytics.bookingHistory.ordersByService?.productsCompleted ?? 0} / Cancelled {analytics.bookingHistory.ordersByService?.productsCancelled ?? 0} / Pending {analytics.bookingHistory.ordersByService?.productsPending ?? 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Analytics */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-primary-500" />
                  Payment Analytics
                </h4>
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center justify-between sm:flex-col sm:items-start sm:gap-1">
                      <span className="text-gray-500 dark:text-gray-400">Total Amount Spent</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        ₹{analytics.payments.totalAmountSpent.toFixed(0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between sm:flex-col sm:items-start sm:gap-1">
                      <span className="text-gray-500 dark:text-gray-400">Payments / Failed</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.payments.totalPayments} / {analytics.payments.failedPayments}
                      </span>
                    </div>
                    <div className="flex items-center justify-between sm:flex-col sm:items-start sm:gap-1">
                      <span className="text-gray-500 dark:text-gray-400">Refund Requests</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.payments.refundRequests}
                      </span>
                    </div>
                    <div className="flex items-center justify-between sm:flex-col sm:items-start sm:gap-1">
                      <span className="text-gray-500 dark:text-gray-400">Preferred Method</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.payments.preferredPaymentMethod || 'NA'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Orders & Bookings table */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Orders &amp; Bookings
                  {analytics.dateRange && (
                    <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                      ({analytics.dateRange.from ? new Date(analytics.dateRange.from).toLocaleDateString() : ''} – {analytics.dateRange.to ? new Date(analytics.dateRange.to).toLocaleDateString() : ''})
                    </span>
                  )}
                </h4>
                {(analytics.ordersByRange?.length ?? 0) === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No orders or bookings in this date range.</p>
                ) : (
                  <div className="max-h-80 overflow-x-auto overflow-y-auto rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 scrollbar-light">
                    <table className="min-w-[36rem] w-full text-xs">
                      <thead className="bg-gray-50 dark:bg-gray-800/60 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Date</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Order ID</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Service</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Status</th>
                          <th className="px-4 py-2 text-right text-gray-500 dark:text-gray-400 font-medium">Amount</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Vendor</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {(analytics.ordersByRange ?? []).map((o) => (
                          <tr key={o.id}>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                              {o.date ? formatDateTime(o.date) : '—'}
                            </td>
                            <td className="px-4 py-2 font-mono text-gray-900 dark:text-white truncate max-w-[140px]" title={o.id}>{o.id}</td>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{o.service}</td>
                            <td className="px-4 py-2">
                              <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                                {o.status}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">
                              {o.amount > 0 ? `₹${o.amount.toFixed(0)}` : '—'}
                            </td>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                              {o.vendor || 'NA'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {!loading && !error && analytics && activeTab === 'behavior-risk' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-primary-500" />
                    Behavior Metrics
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Most Used Service</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.behavior.mostUsedService || 'NA'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Avg Session Time</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.behavior.averageSessionTimeSec >= 60
                          ? `${Math.round(analytics.behavior.averageSessionTimeSec / 60)} min`
                          : analytics.behavior.averageSessionTimeSec > 0
                            ? `${Math.round(analytics.behavior.averageSessionTimeSec)} sec`
                            : '0 min'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Searches per Session</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.behavior.searchesPerSession.toFixed(1)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Conversion Rate</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.behavior.conversionRate.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  {analytics.behavior.metricsFormulas && (
                    <details className="mt-3 text-xs">
                      <summary className="flex items-center gap-1.5 cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                        <Info className="w-3.5 h-3.5" />
                        How we calculate
                      </summary>
                      <ul className="mt-2 space-y-1.5 pl-5 text-gray-500 dark:text-gray-400">
                        {analytics.behavior.metricsFormulas.avgSessionTime && (
                          <li><strong>Avg Session Time:</strong> {analytics.behavior.metricsFormulas.avgSessionTime}</li>
                        )}
                        {analytics.behavior.metricsFormulas.searchesPerSession && (
                          <li><strong>Searches per Session:</strong> {analytics.behavior.metricsFormulas.searchesPerSession}</li>
                        )}
                        {analytics.behavior.metricsFormulas.conversionRate && (
                          <li><strong>Conversion Rate:</strong> {analytics.behavior.metricsFormulas.conversionRate}</li>
                        )}
                        {analytics.behavior.metricsFormulas.mostUsedService && (
                          <li><strong>Most Used Service:</strong> {analytics.behavior.metricsFormulas.mostUsedService}</li>
                        )}
                      </ul>
                    </details>
                  )}

                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mt-6">Recommendations</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Recommendations Seen</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.recommendations.recommendationsSeen}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Clicked</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.recommendations.recommendationsClicked}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Conversions</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.recommendations.recommendationsConverted}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-primary-500" />
                    Risk & Alerts
                  </h4>
                  <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-4 space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Risk Level</span>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                        analytics.risk.flag === 'HIGH'
                          ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                          : analytics.risk.flag === 'MEDIUM'
                          ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300'
                          : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                      }`}>
                        {analytics.risk.flag}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Cancellation Rate</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.risk.cancellationRate.toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Failed Payment Rate</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.risk.failedPaymentRate.toFixed(1)}%
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Flags are derived from high cancellation / failed payment patterns. Use this to quickly spot risky or
                      frustrated users.
                    </p>
                    {analytics.risk.metricsFormulas && (
                      <details className="mt-3 text-xs">
                        <summary className="flex items-center gap-1.5 cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                          <Info className="w-3.5 h-3.5" />
                          How we calculate
                        </summary>
                        <ul className="mt-2 space-y-1.5 pl-5 text-gray-500 dark:text-gray-400">
                          {analytics.risk.metricsFormulas.cancellationRate && (
                            <li><strong>Cancellation Rate:</strong> {analytics.risk.metricsFormulas.cancellationRate}</li>
                          )}
                          {analytics.risk.metricsFormulas.failedPaymentRate && (
                            <li><strong>Failed Payment Rate:</strong> {analytics.risk.metricsFormulas.failedPaymentRate}</li>
                          )}
                          {analytics.risk.metricsFormulas.riskFlag && (
                            <li><strong>Risk Flag:</strong> {analytics.risk.metricsFormulas.riskFlag}</li>
                          )}
                        </ul>
                      </details>
                    )}
                  </div>

                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mt-4">Location & Device</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Most Used City</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.locationAnalytics.mostUsedCity || 'NA'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Last Location</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.locationAnalytics.lastKnownLocation || 'NA'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Location Changes</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.locationAnalytics.locationChanges}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Devices</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.deviceAnalytics.devicesUsed.join(', ') || 'NA'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Network Types</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {analytics.deviceAnalytics.networkTypes.join(', ') || 'NA'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && analytics && activeTab === 'timeline' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary-500" />
                  Sessions &amp; navigations
                </h4>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Show by:
                  </span>
                  <select
                    value={sessionRange}
                    onChange={(e) => setSessionRange(e.target.value as SessionRangeFilter)}
                    className="text-sm rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5"
                  >
                    <option value="day">Today</option>
                    <option value="week">Last 7 days</option>
                    <option value="month">Last 30 days</option>
                    <option value="custom">Custom range</option>
                  </select>
                  {sessionRange === 'custom' && (
                    <>
                      <input
                        type="date"
                        value={customFrom}
                        onChange={(e) => setCustomFrom(e.target.value)}
                        className="text-sm rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5"
                      />
                      <span className="text-gray-400">to</span>
                      <input
                        type="date"
                        value={customTo}
                        onChange={(e) => setCustomTo(e.target.value)}
                        className="text-sm rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5"
                      />
                    </>
                  )}
                </div>
              </div>

              {analytics.sessions && analytics.sessions.length > 0 ? (
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40 overflow-hidden">
                  <p className="text-xs text-gray-500 dark:text-gray-400 px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                    {analytics.sessions.length} session(s). Click a session to open it on a separate page.
                  </p>
                  <ul className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[420px] overflow-y-auto">
                    {analytics.sessions.map((session: UserAnalyticsSession) => (
                      <li key={session.sessionId} className="bg-white dark:bg-gray-800/80">
                        <button
                          type="button"
                          onClick={() => navigate(`/users/${user.userId}/sessions/${encodeURIComponent(session.sessionId)}`, { state: { session, user } })}
                          className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatSessionDate(session.sessionStart)} — {session.eventsCount} event{session.eventsCount !== 1 ? 's' : ''}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {[session.trackedFrom, session.device || 'Device', session.city]
                                .filter(Boolean)
                                .join(' · ') || '—'}
                            </p>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/40 p-6 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No sessions in this period. Try a different date range or ensure events are being tracked.
                  </p>
                </div>
              )}
            </div>
          )}

          {!loading && !error && analytics && activeTab === 'auth-activity' && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Current Status</p>
                  <div className="mt-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      analytics.authActivity?.isLoggedIn
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                        : 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300'
                    }`}>
                      {analytics.authActivity?.isLoggedIn ? 'Logged In' : 'Logged Out'}
                    </span>
                  </div>
                </div>
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Active Devices</p>
                  <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
                    {analytics.authActivity?.activeSessions ?? 0}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">App Version</p>
                  <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                    {(() => {
                      const latest = analytics.authActivity?.recentSessions?.[0];
                      if (!latest?.appVersion) return '—';
                      return `${latest.appVersion}${latest.appBuildNumber ? ` (${latest.appBuildNumber})` : ''}`;
                    })()}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Last Login</p>
                  <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                    {formatDateTime(analytics.authActivity?.lastLogin ?? analytics.activitySummary.lastLogin)}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Last Logout</p>
                  <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
                    {formatDateTime(analytics.authActivity?.lastLogout ?? analytics.activitySummary.lastLogout)}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Per-device sessions</h4>
                {(analytics.authActivity?.recentSessions?.length ?? 0) === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No login/logout sessions found for this user.</p>
                ) : (
                  <div className="max-h-96 overflow-x-auto overflow-y-auto rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 scrollbar-light">
                    <table className="min-w-[64rem] w-full text-xs">
                      <thead className="bg-gray-50 dark:bg-gray-800/60 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Platform</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">App Version</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium min-w-[220px]">OS / Device</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Method</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Device ID</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Session ID</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Status</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Login Time</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Logout Time</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Logout Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {(analytics.authActivity?.recentSessions || []).map((s: UserAuthActivitySession) => (
                          <tr key={s.sessionId}>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300 capitalize">{s.platform || '—'}</td>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">
                              {s.appVersion ? `${s.appVersion}${s.appBuildNumber ? ` (${s.appBuildNumber})` : ''}` : '—'}
                            </td>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300 min-w-[220px]">
                              {s.osVersion || s.deviceModel ? `${s.osVersion || ''} ${s.deviceModel ? `(${s.deviceModel})` : ''}`.trim() : '—'}
                            </td>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{s.loginMethod || '—'}</td>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300 font-mono max-w-[140px] truncate" title={s.deviceId || ''}>
                              {s.deviceId || '—'}
                            </td>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300 font-mono max-w-[160px] truncate" title={s.sessionId}>
                              {s.sessionId}
                            </td>
                            <td className="px-4 py-2">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                s.status === 'ACTIVE'
                                  ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                                  : s.status === 'LOGGED_OUT'
                                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                                  : s.status === 'REVOKED'
                                  ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                                  : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                              }`}>
                                {s.status}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDateTime(s.loginAt)}</td>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDateTime(s.logoutAt)}</td>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{s.logoutReason || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {!loading && !error && analytics && activeTab === 'storage-usage' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Storage limit</p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {formatBytes(storageUsage?.storageLimitBytes ?? 0)}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Used</p>
                  <p className="mt-2 text-2xl font-semibold text-blue-600 dark:text-blue-400">
                    {formatBytes(storageUsage?.totalBytes ?? 0)}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Remaining</p>
                  <p className="mt-2 text-2xl font-semibold text-emerald-600 dark:text-emerald-400">
                    {formatBytes(storageUsage?.remainingBytes ?? 0)}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Health records</p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">{storageUsage?.totalRecords ?? 0}</p>
                </div>
              </div>

              {loadingStorageUsage ? (
                <div className="rounded-lg border border-gray-100 bg-white p-6 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400">
                  Loading storage usage...
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px,1fr]">
                  <div className="rounded-lg border border-gray-100 bg-white p-4 dark:border-gray-700 dark:bg-gray-900/40">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Usage pie chart (100 MB)</p>
                    <div className="mt-4 flex items-center justify-center">
                      <div
                        className="h-44 w-44 rounded-full border border-gray-200 dark:border-gray-600"
                        style={{ background: storagePie }}
                      />
                    </div>
                    <p className="mt-3 text-center text-sm text-gray-600 dark:text-gray-300">
                      Used {Number(storageUsage?.usedPercent || 0).toFixed(1)}% of 100 MB
                    </p>
                  </div>

                  <div className="rounded-lg border border-gray-100 bg-white p-4 dark:border-gray-700 dark:bg-gray-900/40">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Usage by health record type</p>
                    <div className="mt-3 space-y-3">
                      {(storageUsage?.byType || []).map((row) => {
                        const share = storageTypeTotalBytes > 0 ? (Number(row.bytes || 0) / storageTypeTotalBytes) * 100 : 0;
                        return (
                          <div key={row.type} className="rounded-lg border border-gray-100 p-3 dark:border-gray-700">
                            <div className="flex items-center justify-between gap-2 text-sm">
                              <span className="font-medium text-gray-900 dark:text-white">{row.type || 'UNKNOWN'}</span>
                              <span className="text-gray-600 dark:text-gray-300">
                                {row.count} file{row.count === 1 ? '' : 's'} · {formatBytes(Number(row.bytes || 0))}
                              </span>
                            </div>
                            <div className="mt-2 h-2 rounded bg-gray-100 dark:bg-gray-700">
                              <div
                                className="h-2 rounded bg-blue-500"
                                style={{ width: `${Math.min(100, Math.max(0, share))}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                      {(storageUsage?.byType || []).length === 0 && (
                        <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
                          No health records uploaded yet for this user.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {!loading && !error && analytics && activeTab === 'api-metrics' && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total API Calls</p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {Number(actorDashboard?.apiSummary?.total_calls || 0).toLocaleString()}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Failed Calls</p>
                  <p className="mt-2 text-2xl font-semibold text-red-600 dark:text-red-400">
                    {Number(actorDashboard?.apiSummary?.failed_calls || 0).toLocaleString()}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Avg Latency</p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {Number(actorDashboard?.apiSummary?.avg_latency_ms || 0).toFixed(0)} ms
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">P95 Latency</p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {Number(actorDashboard?.apiSummary?.p95_latency_ms || 0).toFixed(0)} ms
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Most Used Endpoints</h4>
                <div className="mt-3 max-h-80 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-800/60 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left">Endpoint</th>
                        <th className="px-4 py-2 text-left">Method</th>
                        <th className="px-4 py-2 text-right">Calls</th>
                        <th className="px-4 py-2 text-right">Errors</th>
                        <th className="px-4 py-2 text-right">Avg Latency</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {(actorDashboard?.topEndpoints || []).map((row) => (
                        <tr key={`${row.method}:${row.endpoint}`}>
                          <td className="px-4 py-2 truncate max-w-[300px]" title={row.endpoint}>{row.endpoint}</td>
                          <td className="px-4 py-2">{row.method}</td>
                          <td className="px-4 py-2 text-right">{Number(row.total_calls || 0).toLocaleString()}</td>
                          <td className="px-4 py-2 text-right">{Number(row.failed_calls || 0).toLocaleString()}</td>
                          <td className="px-4 py-2 text-right">{Number(row.avg_latency_ms || 0).toFixed(0)} ms</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {loadingActorDashboard && <p className="text-sm text-gray-500 dark:text-gray-400">Loading API metrics...</p>}
            </div>
          )}

          {!loading && !error && analytics && activeTab === 'error-log' && (
            <div className="space-y-5">
              <div className="rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-4">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Frequent Errors for this User</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Shows grouped error trends instead of raw event rows.
                </p>
                <div className="mt-3 max-h-80 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-800/60 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left">Error Type</th>
                        <th className="px-4 py-2 text-right">Occurrences</th>
                        <th className="px-4 py-2 text-left">Latest Message</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {(actorDashboard?.errorSummary || []).map((row) => (
                        <tr key={row.error_type}>
                          <td className="px-4 py-2">{row.error_type}</td>
                          <td className="px-4 py-2 text-right">{Number(row.total_occurrences || 0).toLocaleString()}</td>
                          <td className="px-4 py-2 truncate max-w-[420px]" title={row.latest_message}>{row.latest_message || '—'}</td>
                        </tr>
                      ))}
                      {(actorDashboard?.errorSummary || []).length === 0 && (
                        <tr>
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-400" colSpan={3}>
                            No tracked errors in selected period.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {loadingActorDashboard && <p className="text-sm text-gray-500 dark:text-gray-400">Loading error insights...</p>}
            </div>
          )}

          {activeTab === 'notifications' && (
            <RecipientNotificationsList
              recipientType="user"
              recipientId={user.userId}
              recipientName={safe(user.name) !== 'NA' ? safe(user.name) : safe(user.phone_number)}
            />
          )}

          {activeTab === 'permissions' && (
            <PermissionTable
              type="user"
              id={user.userId}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailsModal; 